// @flow
import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { countBy, flow, filter, flatten, get, map, round, orderBy } from 'lodash/fp';
import download from 'downloadjs';
import { connect } from 'react-redux';

import Checkbox from 'app/components/commons/Checkbox';
import Modal from 'app/components/commons/Modal';
import FormValidationMessage from 'app/components/commons/FormValidationMessage';
import { downloadCrunchedLineUps } from 'app/api/challenges';
import Button from 'app/components/commons/Button';
import Rows from 'app/components/layout/Rows';
import Columns from 'app/components/commons/Columns';
import Column from 'app/components/commons/Column';
import { updateEntityProperty } from 'app/actions/challengeActions';
import { LineupDisplayOrder } from 'app/scenes/fantasyInsider/constants';

import TeamTable from './TeamTable';
import ButtonContainer from './ButtonContainer';
import LineUpsExposureTable from './LineUpsExposureTable';
import ExposureMgmtTable from './ExposureMgmtTable';

const LineUpGrid = ({
  lineUps,
  entities,
  allLineUpsSaved,
  keepCurrentLineUps,
  onToggleKeepCurrentLineUps,
  onSaveLineUps,
  onClearLineUps,
  onRecalculate,
  onResetEntered,
  onDeleteLineUp,
  onRemovePlayer,
  onUpdateEntityProperty,
  requiredSelections,
  onCrunchTeams,
  onMarkEntered,
  savedLineUps,
  slateId,
  lineUpErrors,
  sportHasTeam
}: Props) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(undefined);
  const [isViewingExposure, setIsViewingExposure] = useState(false);
  const [exposureVariations, setExposureVariations] = useState([]);
  const [sortedLineups, setSortedLineups] = useState(lineUps);
  const [selectedLineupDisplayOrder, setSelectedSelectionDisplayOrder] = useState(
    LineupDisplayOrder.Default
  );

  const resetVariations = () => setExposureVariations([]);
  const updateExposureVariation = (idStr: string, value: number | undefined) => {
    const variationToUpdate = exposureVariations.find(variation => variation.idStr === idStr);

    if (variationToUpdate && value) {
      variationToUpdate.value = value;
      setExposureVariations([...exposureVariations]);
    } else if (value) {
      setExposureVariations([...exposureVariations, { idStr, value }]);
    } else {
      setExposureVariations(
        exposureVariations.filter(variation => variation !== variationToUpdate)
      );
    }
  };

  const handleSaveAll = () => {
    setIsSaving(true);
    onSaveLineUps(lineUps, true);
  };

  const handleDownloadLineUps = () => {
    setIsDownloading(true);

    downloadCrunchedLineUps({
      lineups: lineUps.map(({ selection }) => ({
        selection: selection.map(({ id, isTeam }) => (isTeam ? `T${id}` : id)),
        featured: false
      })),
      slate_id: slateId
    })
      .then(blob => {
        download(blob, 'LineUps.csv', 'text/csv');
      })
      .catch(({ errors }) => setError(get('message', errors)))
      .finally(() => setIsDownloading(false));
  };

  const handleViewExposure = () => setIsViewingExposure(true);

  const getExposures = (lineUpsData, { filterRemoved = true } = {}) => {
    const enteredCounts = countBy(
      'idStr',
      flow(
        map('selection'),
        flatten,
        filter(({ isRemoved }) => (filterRemoved ? !isRemoved : true))
      )(lineUpsData)
    );

    return orderBy(
      ['exposure'],
      ['desc'],
      Object.entries(enteredCounts).map(([idStr, count]) => {
        const { name, insiderScoreUpdated } = entities.find(e => e.idStr === idStr) || {};
        return {
          idStr,
          name,
          insiderScoreUpdated,
          exposure: round((count * 100) / lineUpsData.length)
        };
      })
    );
  };

  const enteredLineUps = lineUps.filter(({ entered }) => entered);

  const enteredExposures = getExposures(enteredLineUps);
  const allExposures = getExposures(lineUps, { filterRemoved: false });

  const getPlayersExposure = () => {
    const exposures = orderBy(['exposure'], ['desc'], enteredExposures);
    const removed = flow(
      map('selection'),
      flatten,
      filter(({ isRemoved }) => isRemoved)
    )(enteredLineUps);

    if (removed.length === 0) {
      return exposures;
    }

    const totalRequiredSelections = requiredSelections * enteredLineUps.length;
    exposures.push({
      idStr: 'REMOVED_PLAYER',
      name: 'No Player Selected',
      insiderScoreUpdated: '-',
      exposure: round((removed.length * 100) / totalRequiredSelections)
    });

    return exposures;
  };

  const handleResetEntered = () => {
    onResetEntered();
    setIsViewingExposure(false);
  };

  const handleCrunchTeams = (idStr?: string) => {
    const variationsToProcess = exposureVariations.filter(
      // if idStr is not set process them all (happens when APPLY ALL CHANGES clicked)
      expVar => !idStr || expVar.idStr === idStr
    );

    const updatedEntities = entities.map(entity => {
      const updatedExposure = variationsToProcess.find(expVar => expVar.idStr === entity.idStr);
      if (updatedExposure) {
        onUpdateEntityProperty(entity.idStr, 'insiderExposure', updatedExposure.value);
        return { ...entity, insiderExposure: updatedExposure.value };
      }

      return entity;
    });

    onCrunchTeams(updatedEntities);
    resetVariations();
  };

  useEffect(() => setIsSaving(false), [lineUps]);

  useEffect(() => {
    if (selectedLineupDisplayOrder === LineupDisplayOrder.ProjectedHighLow) {
      const lineupCopy = [...lineUps];
      setSortedLineups(
        lineupCopy.sort((l1, l2) => {
          return l2.totalScore - l1.totalScore;
        })
      );
    } else {
      setSortedLineups(lineUps);
    }
  }, [selectedLineupDisplayOrder, lineUps]);

  return (
    <React.Fragment>
      <Columns>
        <Column className="is-two-thirds">
          <h3>YOUR CRUNCHED LINEUPS</h3>
          <ButtonContainer
            allLineUpsSaved={allLineUpsSaved}
            onSaveAll={handleSaveAll}
            isSaving={isSaving}
            selectedSortingOrder={selectedLineupDisplayOrder}
            onSelectLineupOrderChanged={setSelectedSelectionDisplayOrder}
            onDownloadLineUps={handleDownloadLineUps}
            isDownloading={isDownloading}
            onClearLineUps={onClearLineUps}
          />
          {error && <FormValidationMessage kind="danger" message={error} />}
          <Rows mobile={1} desktop={2}>
            {sortedLineups.map(
              ({ selection, totalSalary, totalScore, entered, lineUpId }, index) => (
                <TeamTable
                  key={lineUpId}
                  lineUpId={lineUpId}
                  isSaved={savedLineUps.includes(lineUpId)}
                  error={get(lineUpId, lineUpErrors)}
                  entered={entered}
                  onSaveLineUps={onSaveLineUps}
                  onRemovePlayer={onRemovePlayer}
                  onDeleteLineUp={onDeleteLineUp}
                  onMarkEntered={onMarkEntered}
                  onRecalculate={onRecalculate}
                  onResetEntered={onResetEntered}
                  max
                  onViewExposure={handleViewExposure}
                  order={index + 1}
                  selection={selection}
                  totalSalary={totalSalary}
                  totalScore={totalScore}
                  sportHasTeam={sportHasTeam}
                />
              )
            )}
          </Rows>
          <ButtonContainer
            allLineUpsSaved={allLineUpsSaved}
            onSaveAll={handleSaveAll}
            isSaving={isSaving}
            selectedSortingOrder={selectedLineupDisplayOrder}
            onSelectLineupOrderChanged={setSelectedSelectionDisplayOrder}
            onDownloadLineUps={handleDownloadLineUps}
            isDownloading={isDownloading}
            onClearLineUps={onClearLineUps}
          />
        </Column>
        <Column className="is-one-third">
          <h3>Exposure Management</h3>
          <p>
            Change one/multiple players maximum exposure at a time. Unless &apos;keep current
            lineups&apos; is checked, changing player exposure will re-generate and replace your
            lineups, using your current settings.
          </p>
          <Row>
            <Checkbox checked={keepCurrentLineUps} onChange={onToggleKeepCurrentLineUps}>
              KEEP CURRENT LINEUPS
            </Checkbox>
            {exposureVariations.length > 1 && (
              <Button kind="info" autoBorderRadius={false} onClick={() => handleCrunchTeams()}>
                APPLY ALL CHANGES
              </Button>
            )}
          </Row>
          <ExposureMgmtTable
            playerExposures={allExposures}
            onCrunchTeams={handleCrunchTeams}
            updateExposureVariation={updateExposureVariation}
            exposureVariations={exposureVariations}
          />
        </Column>
      </Columns>
      {isViewingExposure && (
        <Modal
          isOpen
          onCancel={() => setIsViewingExposure(false)}
          shouldCloseOnOverlayClick
          size="medium"
        >
          <div>
            <h3>CURRENT EXPOSURE LEVELS</h3>
            <p>
              The table below shows exposure levels amongst your entered lineups. Click &apos;mark
              lineup as entered&apos; on the lineup cruncher to include it in these calculations.
            </p>
            <LineUpsExposureTable players={getPlayersExposure()} />
            <Button block kind="success" onClick={handleResetEntered}>
              Reset Exposure levels and lineups marked as entered
            </Button>
          </div>
        </Modal>
      )}
    </React.Fragment>
  );
};

const defaultProps = {
  lineUps: [],
  savedLineUps: [],
  lineUpErrors: undefined
};

LineUpGrid.defaultProps = defaultProps;

const actions = {
  onUpdateEntityProperty: updateEntityProperty
};

export default React.memo(connect(null, actions)(LineUpGrid));

type Props = {
  slateId: string | number,
  entities: Array<Object>,
  allLineUpsSaved: boolean,
  requiredSelections: number,
  keepCurrentLineUps: boolean,
  onToggleKeepCurrentLineUps: () => void,
  onCrunchTeams: (players: Array<Object>) => void,
  onSaveLineUps: (data: Object, isSaveAll?: boolean) => void,
  onClearLineUps: () => void,
  onMarkEntered: (lineUpId: string) => void,
  onRecalculate: () => void,
  onResetEntered: () => void,
  onDeleteLineUp: (lineUpId: string) => void,
  onRemovePlayer: (lineUpId: string, playerId: string) => void,
  onUpdateEntityProperty: (field: string, val: string) => void,
  lineUps?: Array<Object>,
  savedLineUps?: Array<string>,
  lineUpErrors?: Object,
  sportHasTeam: boolean
};

const Row = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  justifyContent: 'space-between',
  marginBottom: theme.spacing.small
}));
